/**
 * Account section + lifecycle workflows for a school or district.
 *
 * Shows the account status, owner, alias and activation/close dates, and offers
 * status-appropriate actions:
 *   None   -> Activate account | Start demo
 *   Demo   -> Activate account | Close account
 *   Active -> Edit owner & alias | Close account
 *   Past   -> Reopen account
 *
 * Activation/edit collect the owner (from the advocate list) and alias in a Neon
 * modal. Every action goes through `onUpdateAccount`, which the API layer audits.
 */
import { useState } from 'react';
import { DetailSection, DefinitionList, type ReadItem } from './DetailSection';
import { Modal } from '../neon/Modal';
import { Button, SelectInput, TextInput, NumberInput, StatusTag } from '../neon/controls';
import { useDomId } from '../hooks/useDomId';
import type { Advocate } from '../api/types';

type Mode = null | 'activate' | 'demo' | 'edit';

export interface AccountPanelProps {
  idPrefix: string;
  detail: Record<string, any>;
  advocates: Advocate[];
  /** Schools carry a subscription-status code; districts do not. */
  showSubscriptionStatus?: boolean;
  onUpdateAccount: (patch: Record<string, any>) => Promise<unknown>;
  onChanged: () => void;
}

function advocateLabel(a: Advocate): string {
  const name = [a.firstName, a.lastName].filter(Boolean).join(' ') || `Advocate #${a.id}`;
  return a.abbrev ? `${name} (${a.abbrev})` : name;
}

/** Prefer the DATE column (schools); fall back to the unix-time column (districts). */
function accountDate(detail: Record<string, any>, kind: 'creation' | 'close'): string {
  const d = detail[`account_${kind}_date`];
  if (d) {
    const s = String(d).slice(0, 10);
    // Guard the MySQL zero-date sentinel ('0000-00-00' → serialized as 1899-11-30).
    if (Number(s.slice(0, 4)) >= 1971) return s;
  }
  const t = detail[`account_${kind}_time`];
  if (t && Number(t) > 0) {
    const dt = new Date(Number(t) * 1000);
    if (dt.getUTCFullYear() >= 1971) return dt.toISOString().slice(0, 10);
  }
  return '—';
}

export function AccountPanel({
  idPrefix,
  detail,
  advocates,
  showSubscriptionStatus,
  onUpdateAccount,
  onChanged,
}: AccountPanelProps) {
  const status = Number(detail.is_account);
  const [mode, setMode] = useState<Mode>(null);

  const ownerName = [detail.owner_first_name, detail.owner_last_name].filter(Boolean).join(' ');
  const ownerDisplay = ownerName
    ? detail.owner_abbrev
      ? `${ownerName} (${detail.owner_abbrev})`
      : ownerName
    : 'unassigned';

  const advocateOptions = advocates.map((a) => ({ value: a.id, label: advocateLabel(a) }));

  const readItems: ReadItem[] = [
    { label: 'Status', value: <StatusTag status={detail.accountStatus} /> },
    { label: 'Account owner', value: ownerDisplay },
    { label: 'Alias', value: detail.alias || '—' },
    { label: 'Activated', value: accountDate(detail, 'creation') },
  ];
  if (status === 2) readItems.push({ label: 'Closed', value: accountDate(detail, 'close') });
  if (showSubscriptionStatus)
    readItems.push({ label: 'Subscription status', value: detail.subscription_status ?? '—' });

  const apply = async (patch: Record<string, any>) => {
    await onUpdateAccount(patch);
    setMode(null);
    onChanged();
  };

  const onReopen = async () => {
    if (!window.confirm(`Reopen ${detail.name} as an active account?`)) return;
    await apply({ is_account: 1 });
  };
  const onCloseAccount = async () => {
    if (!window.confirm(`Close (deactivate) ${detail.name}? This sets the account to Past.`)) return;
    await apply({ is_account: 2 });
  };

  const actions = (
    <>
      {(status === 0 || status === 3) && (
        <Button
          id={`${idPrefix}-acct-activate`}
          text="Activate account"
          variant="primary"
          onClick={() => setMode('activate')}
          ariaHaspopup="dialog"
        />
      )}
      {status === 0 && (
        <Button
          id={`${idPrefix}-acct-demo`}
          text="Start demo"
          variant="secondary"
          onClick={() => setMode('demo')}
          ariaHaspopup="dialog"
        />
      )}
      {status === 1 && (
        <Button
          id={`${idPrefix}-acct-edit`}
          text="Edit owner & alias"
          variant="secondary"
          icon="edit"
          onClick={() => setMode('edit')}
          ariaHaspopup="dialog"
        />
      )}
      {status === 2 && (
        <Button id={`${idPrefix}-acct-reopen`} text="Reopen account" variant="primary" onClick={onReopen} />
      )}
      {(status === 1 || status === 3) && (
        <Button id={`${idPrefix}-acct-close`} text="Close account" variant="tertiary" icon="circle-x" onClick={onCloseAccount} />
      )}
    </>
  );

  return (
    <DetailSection title="Account" actions={actions}>
      <DefinitionList items={readItems} />
      {mode && (
        <AccountModal
          mode={mode}
          idPrefix={idPrefix}
          detail={detail}
          advocateOptions={advocateOptions}
          showSubscriptionStatus={showSubscriptionStatus}
          onSave={apply}
          onCancel={() => setMode(null)}
        />
      )}
    </DetailSection>
  );
}

interface AccountModalProps {
  mode: Exclude<Mode, null>;
  idPrefix: string;
  detail: Record<string, any>;
  advocateOptions: Array<{ value: number; label: string }>;
  showSubscriptionStatus?: boolean;
  onSave: (patch: Record<string, any>) => Promise<void>;
  onCancel: () => void;
}

function AccountModal({
  mode,
  idPrefix,
  detail,
  advocateOptions,
  showSubscriptionStatus,
  onSave,
  onCancel,
}: AccountModalProps) {
  const baseId = useDomId(`${idPrefix}-acctform`);
  const [owner, setOwner] = useState<string>(
    detail.account_owner && Number(detail.account_owner) > 0 ? String(detail.account_owner) : '',
  );
  const [alias, setAlias] = useState<string>(detail.alias || '');
  const [subStatus, setSubStatus] = useState<number | undefined>(
    detail.subscription_status != null ? Number(detail.subscription_status) : undefined,
  );
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const requireOwnerAlias = mode === 'activate';
  const showAlias = mode === 'activate' || mode === 'edit';
  const showSub = mode === 'edit' && !!showSubscriptionStatus;

  const title =
    mode === 'activate' ? 'Activate account' : mode === 'demo' ? 'Start demo account' : 'Edit account';
  const saveLabel = mode === 'activate' ? 'Activate' : mode === 'demo' ? 'Start demo' : 'Save account';

  const submit = async () => {
    setError('');
    if (requireOwnerAlias && !owner) {
      setError('Select an account owner to activate.');
      return;
    }
    if (requireOwnerAlias && !alias.trim()) {
      setError('Enter an alias (used in product / SSO URLs).');
      return;
    }
    const patch: Record<string, any> = {};
    if (mode === 'activate') patch.is_account = 1;
    if (mode === 'demo') patch.is_account = 3;
    patch.account_owner = owner ? Number(owner) : 0;
    if (showAlias) patch.alias = alias.trim();
    if (showSub) patch.subscription_status = subStatus ?? null;
    setBusy(true);
    try {
      await onSave(patch);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the account.');
      setBusy(false);
    }
  };

  const footer = (
    <>
      <Button id={`${baseId}-save`} text={saveLabel} variant="primary" onClick={submit} loading={busy} />
      <Button id={`${baseId}-cancel`} text="Cancel" variant="tertiary" onClick={onCancel} />
    </>
  );

  return (
    <Modal title={title} size="small" onClose={onCancel} footer={footer}>
      <div className="modal-form">
        {error ? <p className="form-error">{error}</p> : null}
        {mode === 'demo' ? (
          <p className="subtle">Start a demo/trial account. You can activate it fully later.</p>
        ) : null}
        <SelectInput
          id={`${baseId}-owner`}
          label="Account owner"
          value={owner}
          onChange={setOwner}
          options={advocateOptions}
          allowEmpty
          emptyText="— unassigned —"
          showFilter
        />
        {showAlias ? (
          <TextInput
            id={`${baseId}-alias`}
            label="Alias"
            value={alias}
            onChange={setAlias}
            required={requireOwnerAlias}
            helper="Short code used in product / SSO URLs."
          />
        ) : null}
        {showSub ? (
          <NumberInput
            id={`${baseId}-substatus`}
            label="Subscription status (code)"
            value={subStatus}
            onChange={setSubStatus}
            integer
          />
        ) : null}
      </div>
    </Modal>
  );
}
